// CallRoute.tsx · v12.10.22.2 (#2060 an ending without "sequence complete" - voicemail / transfer / end-call - still gets its End lane: the engine's "terminal: <key>" line, or an older call's "terminal <key>" line) · 2026-10-28 · VoiceSpectrum AI
// CallRoute.tsx · v12.10.22.1 (#2055 the opened card follows the Call path switch - off: only what was said; on: the call path lines too. Tushar 10/28: "if call path is disabled then call route should not display call path") · 2026-10-28 · VoiceSpectrum AI
// CallRoute.tsx · v12.10.22 (#2010 the Call route in the message drawer - prototype UI-PROTO-CALL-ROUTE-T173 v1.2, layout C) · 2026-10-28 · VoiceSpectrum AI
//
// The route is built ONLY from the call path the call already recorded (the [path] lines the engine writes) - no new data.
// Every card in order, grouped Pre-routing · Request 1..n · End; a click opens that card's own lines (what was said + the path
// notes) under it. Owner only: the drawer renders this behind the "Call path" right, and the API does not send path lines to
// anyone without it (ApplyMessageRights), so a user without the right has nothing to build it from.
import { useMemo, useState } from 'react';

export interface RouteLine { ts: string | null; speaker: string; role: 'agent' | 'caller' | 'path'; text: string }

type NodeState = 'ran' | 'skip' | 'off';
interface RouteNode { key: string; name: string; state: NodeState; say: string; at: string; lines: RouteLine[] }
interface RouteHop { key: string; text: string; at: string; lines: RouteLine[] }
interface RouteLane { key: string; title: string; items: (RouteNode | RouteHop)[] }

const isPathLine = (l: RouteLine) => l.role === 'path' || (l.text ?? '').trimStart().startsWith('[path]');
const pathText = (l: RouteLine) => (l.text ?? '').replace(/^\s*\[path\]\s*/, '').trim();
const isNode = (x: RouteNode | RouteHop): x is RouteNode => (x as RouteNode).state !== undefined;

/** mm:ss from the first line of the call. */
function offsetLabel(ts: string | null, t0: number | null): string {
  if (!ts || t0 === null) return '';
  const t = new Date(ts).getTime();
  if (isNaN(t)) return '';
  const s = Math.max(0, Math.round((t - t0) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

const CARD_NUM = /^card (\d+)\/(\d+) "(.+?)" \((.+?)\) -> (.*)$/i;
const CARD_PRE = /^card "(.+?)" -> (.*)$/i;
const SEQ_DONE = /^sequence complete:.*-> terminal (.+)$/i;
const HOP = /^(classify|wrapup|route)\s*:/i;
const TERMINAL = /^terminal:?\s+([a-z][a-z-]*)\b/i;   // v12.10.22.2 (#2060)

/** Pure: the lanes of the route from the stored lines (exported for tests / print). */
export function buildRoute(lines: RouteLine[]): { lanes: RouteLane[]; cards: number; skipped: number; off: number; requests: number } {
  const t0 = (() => { for (const l of lines) { const t = l.ts ? new Date(l.ts).getTime() : NaN; if (!isNaN(t)) return t; } return null; })();
  const lanes: RouteLane[] = [{ key: 'pre', title: 'Pre-routing', items: [] }];
  let lane = lanes[0];
  let cur: RouteNode | RouteHop | null = null;
  let requests = 0, cards = 0, skipped = 0, off = 0, k = 0;
  let lastHop: RouteHop | null = null;
  const push = (x: RouteNode | RouteHop) => { lane.items.push(x); cur = x; };
  lines.forEach((l) => {
    const at = offsetLabel(l.ts, t0);
    if (!isPathLine(l)) {
      if (cur) cur.lines.push(l);
      else push({ key: `n${k++}`, name: 'Call start', state: 'ran', say: '', at, lines: [l] });
      return;
    }
    const t = pathText(l);
    let m: RegExpExecArray | null;
    if ((m = CARD_NUM.exec(t))) {
      const idx = Number(m[1]);
      if (idx === 1 || lane.key === 'pre' || lane.key === 'end') {
        requests += 1;
        const title = lastHop ? lastHop.text.replace(/^(classify|wrapup|route)\s*:\s*/i, '') : '';
        lane = { key: `r${requests}`, title: `Request ${requests}${title ? ` · ${shorten(title, 90)}` : ''}`, items: [] };
        lanes.push(lane);
      }
      const rest = m[5];
      const state: NodeState = /disabled/i.test(rest) ? 'off' : /skipped/i.test(rest) ? 'skip' : 'ran';
      cards += 1; if (state === 'skip') skipped += 1; if (state === 'off') off += 1;
      push({ key: `n${k++}`, name: `${idx} ${m[3]}`, state, say: `${m[4]} · ${rest}`, at, lines: [l] });
      return;
    }
    if ((m = CARD_PRE.exec(t))) {
      const state: NodeState = /disabled/i.test(m[2]) ? 'off' : /skipped/i.test(m[2]) ? 'skip' : 'ran';
      cards += 1; if (state === 'skip') skipped += 1; if (state === 'off') off += 1;
      if (lane.key !== 'pre') { lane = lanes[0]; }
      push({ key: `n${k++}`, name: m[1], state, say: m[2], at, lines: [l] });
      return;
    }
    if ((m = SEQ_DONE.exec(t))) {
      lane = { key: 'end', title: 'End', items: [] };
      lanes.push(lane);
      push({ key: `n${k++}`, name: `Terminal · ${m[1]}`, state: 'ran', say: t, at, lines: [l] });
      return;
    }
    // v12.10.22.2 (#2060): an ending reached without "sequence complete" opens the End lane on its first terminal line.
    if (lane.key !== 'end' && (m = TERMINAL.exec(t))) {
      lane = { key: 'end', title: 'End', items: [] };
      lanes.push(lane);
      push({ key: `n${k++}`, name: `Terminal · ${m[1]}`, state: 'ran', say: t, at, lines: [l] });
      return;
    }
    if (HOP.test(t) && t.includes('->') && (cur === null || isNode(cur))) {
      const hop: RouteHop = { key: `h${k++}`, text: t, at, lines: [l] };
      lastHop = hop;
      push(hop);
      return;
    }
    if (HOP.test(t) && t.includes('->') && cur && !isNode(cur)) { (cur as RouteHop).text = t; lastHop = cur as RouteHop; }
    if (/skipped/i.test(t) && cur && isNode(cur) && cur.state === 'ran' && cur.lines.length === 1) { cur.state = 'skip'; cur.say = t; skipped += 1; }
    if (cur) cur.lines.push(l);
    else push({ key: `n${k++}`, name: t.split(/[:\-]/)[0].trim() || 'Call start', state: 'ran', say: t, at, lines: [l] });
  });
  return { lanes: lanes.filter((x) => x.items.length > 0), cards, skipped, off, requests };
}

function shorten(s: string, n: number): string { return s.length <= n ? s : s.slice(0, n - 1) + '…'; }

export function CallRoute({ lines, callerLabel, duration, showPath = true }: { lines: RouteLine[]; callerLabel?: string; duration?: string; showPath?: boolean }) {
  const route = useMemo(() => buildRoute(lines), [lines]);
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const toggle = (key: string) => setOpen((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  if (route.lanes.length === 0) return <p className="aw-muted">No call path was recorded for this call.</p>;
  const dot = { ran: '✓', skip: '–', off: '' } as const;
  const lineEl = (l: RouteLine, i: number) => (
    <div key={i} className={`aw-rt-line${isPathLine(l) ? ' is-path' : l.role === 'caller' ? ' is-caller' : ''}`}>
      <div className="aw-rt-spk">{isPathLine(l) ? 'CALL PATH' : l.role === 'caller' ? (callerLabel || 'CALLER').toUpperCase() : (l.speaker || 'AI ASSISTANT').toUpperCase()}</div>
      {isPathLine(l) ? pathText(l) : l.text}
    </div>
  );
  return (
    <div className="aw-route" aria-label="Call route">
      <div className="aw-rt-sum">
        <span>{route.requests} {route.requests === 1 ? 'request' : 'requests'}</span>
        <span>{route.cards} cards · {route.skipped} skipped · {route.off} disabled</span>
        {duration && <span>{duration}</span>}
      </div>
      {route.lanes.map((lane) => (
        <div key={lane.key} className="aw-rt-lane">
          <h4>{lane.title}</h4>
          {lane.items.map((x) => {
            const isOpen = open.has(x.key);
            // v12.10.22.1 (#2055): with the Call path switch off an opened card shows only what was said, never the path lines.
            const spoken = showPath ? x.lines : x.lines.filter((l) => !isPathLine(l));
            if (!isNode(x)) {
              return (
                <div key={x.key} className={`aw-rt-hop${isOpen ? ' is-open' : ''}`} role="button" tabIndex={0}
                  onClick={() => toggle(x.key)} onKeyDown={(e) => { if (e.key === 'Enter') toggle(x.key); }}>
                  <div className="aw-rt-hop-t">{x.text}<span className="aw-rt-at">{x.at} {isOpen ? '▾' : '▸'}</span></div>
                  {isOpen && <div className="aw-rt-exp" onClick={(e) => e.stopPropagation()}>{spoken.length ? spoken.map(lineEl) : <div className="aw-rt-none">No lines spoken here. Switch Call path on to see the routing lines.</div>}</div>}
                </div>
              );
            }
            return (
              <div key={x.key} className={`aw-rt-node is-${x.state}${isOpen ? ' is-open' : ''}`} role="button" tabIndex={0}
                onClick={() => toggle(x.key)} onKeyDown={(e) => { if (e.key === 'Enter') toggle(x.key); }}>
                <div className="aw-rt-dot" aria-hidden="true">{dot[x.state]}</div>
                <div className="aw-rt-main">
                  <span className="aw-rt-name">{x.name}</span>
                  <div className="aw-rt-say">{x.say}</div>
                  {isOpen && (
                    <div className="aw-rt-exp" onClick={(e) => e.stopPropagation()}>
                      {spoken.filter((l) => !isPathLine(l)).length === 0 && <div className="aw-rt-none">No lines spoken on this card.</div>}
                      {spoken.map(lineEl)}
                    </div>
                  )}
                </div>
                <div className="aw-rt-at">{x.at} {isOpen ? '▾' : '▸'}</div>
              </div>
            );
          })}
        </div>
      ))}
      <div className="aw-rt-leg">
        <span><i className="aw-rt-dot is-ran" />ran</span>
        <span><i className="aw-rt-dot is-skip" />skipped (why shown)</span>
        <span><i className="aw-rt-dot is-off" />disabled</span>
      </div>
    </div>
  );
}
